import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Eye, Trash, ArrowsClockwise, DownloadSimple, MagnifyingGlass, WarningCircle, X, Plus } from '@phosphor-icons/react';
import { CellRevealIcon } from '../ui/CellRevealLogo';
import { jobsApi, deconvolutionApi, signaturesApi } from '../../services/endpoints';
import type { Job, Signature } from '../../services/types';

type EnrichedJob = Job & {
  deconvolution_progress?: number;
  deconvolution_error?: string;
  signature_name?: string;
};

const STATUS_STYLES: Record<string, string> = {
  completed:  'bg-teal-50 border-teal-200/60 text-teal-700',
  processing: 'bg-blue-50 border-blue-200/60 text-blue-700',
  pending:    'bg-amber-50 border-amber-200/60 text-amber-700',
  failed:     'bg-red-50 border-red-200/60 text-red-700',
  cancelled:  'bg-muted border-border text-muted-foreground',
};

export function JobQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [jobs, setJobs] = useState<EnrichedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Elapsed runtime of a still-running job depends on the current time. Holding
  // it in state keeps render pure; the poll below advances it.
  const [now, setNow] = useState(() => Date.now());
  // A ref (not state) so the polling interval's closure always sees the latest
  // cache without needing to be a useEffect/fetchJobs dependency.
  const signatureCacheRef = useRef<Record<string, Signature>>({});

  const fetchJobs = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const userId = parseInt(user?.id || '1', 10);
      const response = await jobsApi.listJobs(userId, 0, 100);
      const jobsList = response.data.data || [];

      const enrichedJobs = await Promise.all(
        jobsList.map(async (job): Promise<EnrichedJob> => {
          try {
            const deconvStatus = await deconvolutionApi.getJobStatus(job.job_id);
            const sigId = String(job.signature_id);
            let signatureName = signatureCacheRef.current[sigId]?.name;
            if (!signatureName) {
              try {
                const sigResponse = await signaturesApi.get(sigId);
                const signature = sigResponse.data;
                signatureCacheRef.current[sigId] = signature;
                signatureName = signature.name;
              } catch {
                // signature fetch failed — leave as undefined
              }
            }
            return {
              ...job,
              deconvolution_progress: deconvStatus.data?.progress ?? job.progress,
              deconvolution_error: deconvStatus.data?.error_message ?? job.error_message,
              signature_name: signatureName,
            };
          } catch {
            return job;
          }
        })
      );

      setJobs(enrichedJobs);
      setError(null);
    } catch {
      if (!silent) setError('Failed to load analyses. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(() => {
      setNow(Date.now());
      fetchJobs(true);
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const filteredJobs = jobs.filter(job => {
    const jobId = job.job_id || '';
    const matchesSearch = jobId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || job.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (jobId: string) => {
    setActionError(null);
    try {
      await jobsApi.deleteJob(jobId);
      setJobs(jobs.filter(job => job.job_id !== jobId));
    } catch {
      setActionError('Failed to delete this analysis. Please try again.');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleCancel = async (jobId: string) => {
    setActionError(null);
    try {
      await jobsApi.cancelJob(jobId);
      fetchJobs();
    } catch {
      setActionError('Failed to cancel this analysis. Please try again.');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getSampleNumber = (job: Job) => job.n_samples ?? '—';

  const calculateRuntime = (job: Job) => {
    if (job.completed_at && job.started_at) {
      const diffMs = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
      const m = Math.floor(diffMs / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      return `${m}m ${s}s`;
    }
    if (job.started_at) {
      const diffMs = now - new Date(job.started_at).getTime();
      const m = Math.floor(diffMs / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      return `${m}m ${s}s`;
    }
    return '—';
  };

  const statusCounts = {
    all: jobs.length,
    pending: jobs.filter(j => j.status === 'pending').length,
    processing: jobs.filter(j => j.status === 'processing').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 h-10 w-48 bg-muted rounded-lg animate-pulse" />
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" style={{ opacity: 1 - i * 0.18 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex justify-between items-center mb-8">
          <div>
            <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-2">
              Analysis queue
            </span>
            <h1 className="text-foreground text-2xl font-bold tracking-tight">My analyses</h1>
          </div>
          <Button onClick={() => navigate('/upload')}>
            <Plus className="w-4 h-4" />
            New analysis
          </Button>
        </div>

        {/* Fetch error */}
        {error && (
          <div className="flex items-start gap-3 p-4 mb-6 bg-red-50 border border-red-200/80 rounded-lg">
            <WarningCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={() => fetchJobs()}
                className="mt-2 text-xs font-medium text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Action error */}
        {actionError && (
          <div className="flex items-start gap-3 p-4 mb-6 bg-red-50 border border-red-200/80 rounded-lg">
            <WarningCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm flex-1">{actionError}</p>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search + filter bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by job ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-background transition-colors"
            />
          </div>
          <div className="flex gap-0 border-b border-border shrink-0">
            {(['all', 'processing', 'completed', 'pending', 'failed'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-2 text-sm transition-colors border-b-2 -mb-px capitalize ${
                  filterStatus === status
                    ? 'border-teal-600 text-teal-700 font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {status}
                <span className="ml-1.5 font-mono text-[10px] opacity-50">({statusCounts[status]})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Jobs table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b border-border bg-muted/50`}>
                  {['Job', 'Submitted', 'Status', 'Runtime', 'Algorithm', 'Signature', 'Samples', ''].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/50 uppercase">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredJobs.map(job => {
                  const jobId = job.job_id;
                  const isConfirmingDelete = confirmDeleteId === jobId;
                  return (
                    <tr key={jobId} className="hover:bg-muted/30 transition-colors group">
                      <td className="px-5 py-4">
                        <span className="font-mono text-sm text-foreground">{jobId.slice(0, 8)}</span>
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(job.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <Badge className={STATUS_STYLES[job.status] || ''}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </Badge>
                        {job.status === 'processing' && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all duration-500"
                                style={{ width: `${job.deconvolution_progress || job.progress || 0}%` }}
                              />
                            </div>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {job.deconvolution_progress || job.progress || 0}%
                            </span>
                          </div>
                        )}
                        {job.status === 'failed' && (job.error_message || job.deconvolution_error) && (
                          <p className="mt-1 text-xs text-red-600 max-w-xs truncate">
                            {job.error_message || job.deconvolution_error}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-muted-foreground">
                        {calculateRuntime(job)}
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs px-2 py-0.5 bg-muted rounded border border-border text-foreground">
                          {job.algorithm.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground">
                        {job.signature_name || (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-muted-foreground">
                        {getSampleNumber(job)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          {job.status === 'completed' && (
                            <>
                              <button
                                onClick={() => navigate(`/results/${jobId}`)}
                                className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                title="View results"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => jobsApi.getResults(jobId)}
                                className="p-1.5 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                                title="Download"
                              >
                                <DownloadSimple className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {(job.status === 'processing' || job.status === 'pending') && (
                            <button
                              onClick={() => handleCancel(jobId)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {job.status === 'failed' && (
                            <button
                              className="p-1.5 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                              title="Retry"
                            >
                              <ArrowsClockwise className="w-4 h-4" />
                            </button>
                          )}

                          {/* Inline delete confirmation */}
                          {isConfirmingDelete ? (
                            <div className="flex items-center gap-1 ml-1">
                              <button
                                onClick={() => handleDelete(jobId)}
                                className="px-2 py-1 text-[11px] font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2 py-1 text-[11px] font-medium bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors"
                              >
                                Keep
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(jobId)}
                              className="p-1.5 text-muted-foreground/50 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredJobs.length === 0 && (
            <div className="py-16 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-4">
                <CellRevealIcon variant="light" size="sm" />
              </div>
              {searchTerm || filterStatus !== 'all' ? (
                <>
                  <p className="text-foreground font-medium mb-1">No analyses found</p>
                  <p className="text-muted-foreground text-sm">Try adjusting the search or filter.</p>
                </>
              ) : (
                <>
                  <p className="text-foreground font-medium mb-1">No analyses yet</p>
                  <p className="text-muted-foreground text-sm mb-4">Start your first deconvolution to see results here.</p>
                  <Button onClick={() => navigate('/upload')}>
                    <Plus className="w-4 h-4" />
                    New analysis
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Status legend */}
        <div className="mt-6 pt-5 border-t border-border flex flex-wrap gap-x-6 gap-y-1">
          {[
            ['Pending', 'Queued, waiting to start'],
            ['Processing', 'Analysis in progress'],
            ['Completed', 'Results ready to view'],
            ['Failed', 'Error during processing'],
          ].map(([status, desc]) => (
            <p key={status} className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{status}: </span>
              {desc}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}
